import type { ChatMessageView } from '@/hooks/useFirestoreP2PChat';
import { OPTIMISTIC_CHAT_ATTACHMENT_PREFIX } from '@/services/chatMessagePayload';

/** Исходящий пузырь с локальным превью до записи в Storage/Firestore — нельзя редактировать/удалять как серверное. */
export function isOptimisticOutgoingChatBubble(m: ChatMessageView): boolean {
  if (!m.isSelf) return false;
  const paths = [
    m.attachment?.storagePath,
    ...(m.attachments?.map((a) => a.storagePath) ?? []),
  ];
  return paths.some(
    (p) => typeof p === 'string' && p.startsWith(OPTIMISTIC_CHAT_ATTACHMENT_PREFIX)
  );
}

function attachmentCount(m: ChatMessageView): number {
  if (m.attachments?.length) return m.attachments.length;
  return m.attachment ? 1 : 0;
}

/**
 * Пока оптимистичный пузырь ещё без `serverEchoId`, серверная копия может прийти из снапшота раньше
 * обновления стейта — скрываем дубликат из `messages`, оставляем превью + лоадер.
 */
export function shouldSuppressServerMessageForOptimisticDup(
  serverMsg: ChatMessageView,
  outgoingMediaBubbles: ChatMessageView[]
): boolean {
  if (!serverMsg.isSelf) return false;
  const sc = attachmentCount(serverMsg);
  if (sc === 0) return false;
  return outgoingMediaBubbles.some(
    (o) =>
      !o.serverEchoId &&
      o.isSelf &&
      attachmentCount(o) === sc &&
      o.text.trim() === serverMsg.text.trim() &&
      serverMsg.ts + 10_000 >= o.ts
  );
}

/** Пока оптимистичный пузырь с тем же id документа ещё в стейте — не показывать серверную копию (избегаем схлопывания превью). */
export function shouldSuppressServerMessageForLinkedOptimistic(
  serverMsg: ChatMessageView,
  outgoingMediaBubbles: ChatMessageView[]
): boolean {
  return outgoingMediaBubbles.some(
    (o) => typeof o.serverEchoId === 'string' && o.serverEchoId === serverMsg.id
  );
}

export function buildChatDisplayMessages(
  messages: ChatMessageView[],
  outgoingMediaBubbles: ChatMessageView[]
): ChatMessageView[] {
  const filteredMessages = messages.filter(
    (m) =>
      !shouldSuppressServerMessageForOptimisticDup(m, outgoingMediaBubbles) &&
      !shouldSuppressServerMessageForLinkedOptimistic(m, outgoingMediaBubbles)
  );
  /** Не сортируем по ts: у оптимизма клиентское время, у эхо — серверное; сортировка дала бы скачок позиции. */
  return [...filteredMessages, ...outgoingMediaBubbles];
}
